import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Autocomplete, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, Link as MuiLink, List, ListItem, ListItemAvatar, ListItemText, Stack,
  TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { PersonRemoveOutlined as RemoveIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import unifiedApi, { buildApiUrl } from '../../utils/unifiedApi';
import { initialsFor } from '../../utils/chatFormat';
import { channelTypeLabel, defaultChannelName } from '../../utils/chatChannelTypes';

export const MAX_CHANNEL_NAME_LENGTH = 100;
export const MAX_CHANNEL_DESCRIPTION_LENGTH = 500;

const UNAVAILABLE_MESSAGE = 'This channel is no longer available, or you can\'t manage it.';

const fullName = (person) => `${person.firstName || ''} ${person.lastName || ''}`.trim() || 'Member';

const displayOnlyChip = { clickable: false, onClick: () => {} };

const PersonAvatar = ({ person }) => (
  <Avatar src={person.photoUrl ? buildApiUrl(person.photoUrl) : undefined} alt={fullName(person)}>
    {initialsFor(fullName(person))}
  </Avatar>
);

const PersonLink = ({ person }) => (
  <MuiLink component={RouterLink} to={`/player/${person.userTenantId}`} underline="hover" sx={{ wordBreak: 'break-word' }}>
    {fullName(person)}
  </MuiLink>
);

/**
 * Channel settings for channel admins, League Managers, and organization admins: custom name and
 * description, channel admins, and the (read-only) member list with how each person qualifies.
 */
const ChatChannelSettingsDialog = ({
  open, channelId, onClose, onSaved,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [adminCandidate, setAdminCandidate] = useState(null);
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  // Bumped whenever the dialog closes or switches channel so late responses for an earlier
  // channel (loads and mutations, including their loading/error updates) are ignored.
  const sessionRef = useRef(0);

  // Only opening a channel or saving its details resets the name/description draft; admin
  // changes refresh the rest of the settings without discarding unsaved edits.
  const applySettings = useCallback((data, { resetDraft }) => {
    setSettings(data);
    if (resetDraft) {
      setName(data.channel?.customName || '');
      setDescription(data.channel?.description || '');
    }
  }, []);

  const load = useCallback(async (session, { resetDraft }) => {
    const isCurrent = () => sessionRef.current === session;
    setLoading(true);
    setError(null);
    try {
      const data = await unifiedApi.getChatChannelSettings(channelId);
      if (!isCurrent()) return;
      if (!data) {
        setUnavailable(true);
        return;
      }
      setUnavailable(false);
      applySettings(data, { resetDraft });
    } catch (err) {
      if (isCurrent()) setError(err.message || 'Failed to load channel settings.');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [channelId, applySettings]);

  useEffect(() => {
    if (!open || !channelId) return undefined;
    sessionRef.current += 1;
    const session = sessionRef.current;
    setSettings(null);
    setUnavailable(false);
    setError(null);
    setSuccess(null);
    setName('');
    setDescription('');
    setAdminCandidate(null);
    setSaving(false);
    setAddingAdmin(false);
    setRemovingId(null);
    load(session, { resetDraft: true });
    return () => { sessionRef.current += 1; };
  }, [open, channelId, load]);

  const channel = settings?.channel;
  const members = useMemo(() => settings?.members || [], [settings]);
  const admins = settings?.admins || [];
  const candidates = useMemo(() => members.filter((m) => !m.isAdmin), [members]);
  const defaultName = defaultChannelName(channel);

  // Runs a mutation for the current session; returns null when the dialog moved on meanwhile.
  const mutate = async (action, successMessage, fallbackError, { resetDraft }) => {
    const session = sessionRef.current;
    const isCurrent = () => sessionRef.current === session;
    setError(null);
    setSuccess(null);
    try {
      const data = await action();
      if (!isCurrent()) return null;
      if (!data) {
        setError(UNAVAILABLE_MESSAGE);
        return false;
      }
      applySettings(data, { resetDraft });
      setSuccess(successMessage);
      onSaved?.();
      return true;
    } catch (err) {
      if (!isCurrent()) return null;
      setError(err.message || fallbackError);
      return false;
    }
  };

  const saveDetails = async () => {
    setSaving(true);
    const result = await mutate(
      () => unifiedApi.updateChatChannelSettings(channelId, {
        name: name.trim() || null,
        description: description.trim() || null,
      }),
      'Channel details saved.',
      'Failed to save channel details.',
      { resetDraft: true },
    );
    if (result !== null) setSaving(false);
  };

  const addAdmin = async () => {
    if (!adminCandidate) return;
    setAddingAdmin(true);
    const added = await mutate(
      () => unifiedApi.addChatChannelAdmin(channelId, adminCandidate.userTenantId),
      `${fullName(adminCandidate)} is now a channel admin.`,
      'Failed to add channel admin.',
      { resetDraft: false },
    );
    if (added === null) return;
    if (added) setAdminCandidate(null);
    setAddingAdmin(false);
  };

  // DELETE returns no body (and 404 also maps to null), so reload to show the current admins.
  const removeAdmin = async (admin) => {
    const session = sessionRef.current;
    const isCurrent = () => sessionRef.current === session;
    setRemovingId(admin.userTenantId);
    setError(null);
    setSuccess(null);
    try {
      await unifiedApi.removeChatChannelAdmin(channelId, admin.userTenantId);
      if (!isCurrent()) return;
      setSuccess(`${fullName(admin)} is no longer a channel admin.`);
      onSaved?.();
      await load(session, { resetDraft: false });
    } catch (err) {
      if (isCurrent()) setError(err.message || 'Failed to remove channel admin.');
    } finally {
      if (isCurrent()) setRemovingId(null);
    }
  };

  const detailsChanged = channel
    && ((name.trim() || '') !== (channel.customName || '') || (description.trim() || '') !== (channel.description || ''));

  const renderBody = () => {
    if (loading && !settings) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (unavailable) return <Alert severity="info">{UNAVAILABLE_MESSAGE}</Alert>;
    if (!settings) return null;

    return (
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>Details</Typography>
          <Stack spacing={2}>
            <TextField
              label="Channel name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={defaultName || ''}
              helperText={`Leave blank to use ${defaultName ? `"${defaultName}"` : 'the default name'}.`}
              inputProps={{ maxLength: MAX_CHANNEL_NAME_LENGTH }}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
            <TextField
              label="Description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              helperText={`${description.length}/${MAX_CHANNEL_DESCRIPTION_LENGTH}`}
              inputProps={{ maxLength: MAX_CHANNEL_DESCRIPTION_LENGTH }}
              size="small"
              multiline
              minRows={2}
              fullWidth
            />
            <Box>
              <Button
                variant="contained"
                onClick={saveDetails}
                disabled={saving || !detailsChanged}
                fullWidth={isMobile}
              >
                {saving ? 'Saving…' : 'Save details'}
              </Button>
            </Box>
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle2">Channel admins</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Channel admins can delete any message, edit these settings, and add or remove other admins.
          </Typography>
          {admins.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No channel admins yet.</Typography>
          ) : (
            <List dense disablePadding aria-label="Channel admins">
              {admins.map((admin) => (
                <ListItem
                  key={admin.userTenantId}
                  disableGutters
                  secondaryAction={(
                    <Tooltip title="Remove admin">
                      <span>
                        <IconButton
                          edge="end"
                          onClick={() => removeAdmin(admin)}
                          disabled={removingId === admin.userTenantId}
                          aria-label={`Remove ${fullName(admin)} as admin`}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                >
                  <ListItemAvatar><PersonAvatar person={admin} /></ListItemAvatar>
                  <ListItemText
                    primary={<PersonLink person={admin} />}
                    secondary={admin.isMember ? null : (
                      <Chip
                        component="span"
                        label="No longer a member"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                        {...displayOnlyChip}
                      />
                    )}
                    sx={{ pr: 4 }}
                  />
                </ListItem>
              ))}
            </List>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
            <Autocomplete
              options={candidates}
              value={adminCandidate}
              onChange={(_event, value) => setAdminCandidate(value)}
              getOptionLabel={fullName}
              isOptionEqualToValue={(option, value) => option.userTenantId === value.userTenantId}
              noOptionsText="No other members"
              size="small"
              sx={{ flex: 1 }}
              renderInput={(params) => <TextField {...params} label="Add admin" placeholder="Search members" />}
            />
            <Button
              variant="outlined"
              onClick={addAdmin}
              disabled={!adminCandidate || addingAdmin}
              fullWidth={isMobile}
            >
              {addingAdmin ? 'Adding…' : 'Add'}
            </Button>
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Members ({members.length})
          </Typography>
          {members.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No members.</Typography>
          ) : (
            <List
              dense
              disablePadding
              aria-label="Channel members"
              sx={{
                maxHeight: { xs: 'none', sm: 320 }, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1,
              }}
            >
              {members.map((member) => (
                <ListItem key={member.userTenantId} sx={{ alignItems: 'flex-start' }}>
                  <ListItemAvatar><PersonAvatar person={member} /></ListItemAvatar>
                  <ListItemText
                    primary={<PersonLink person={member} />}
                    secondary={(
                      <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {member.isAdmin && (
                          <Chip component="span" label="Admin" size="small" color="primary" {...displayOnlyChip} />
                        )}
                        {(member.sources || []).map((source) => (
                          <Chip component="span" key={source} label={source} size="small" variant="outlined" {...displayOnlyChip} />
                        ))}
                      </Box>
                    )}
                    secondaryTypographyProps={{ component: 'span' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={isMobile} aria-labelledby="chat-channel-settings-title">
      <DialogTitle id="chat-channel-settings-title">Channel settings</DialogTitle>
      <DialogContent dividers>
        {channel && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, minWidth: 0 }}>
            <Typography fontWeight={600} sx={{ wordBreak: 'break-word' }}>{channel.name}</Typography>
            <Chip label={channelTypeLabel(channel.channelType)} size="small" {...displayOnlyChip} />
          </Stack>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
        {renderBody()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChatChannelSettingsDialog;
